import * as estimateComparisonActions from "common/reducers/estimateComparison.actions";
import {
  EstimateComparisonRecord,
  estimateComparisonFromJSON,
  toJSON as estimateComparisonToJSON,
} from "common/records/EstimateComparison";
import { EstimateComparisonLinkRecord, estimateComparisonLinkFromJSON } from "common/records/EstimateComparisonLink";
import { List, Map, Record } from "immutable";
import { RootState, RootActions } from "common/reducers";

export const EstimateComparisonStateRecord = Record({
  byJobId: Map<number, EstimateComparisonRecord>(),
  lastSavedByJobId: Map<number, EstimateComparisonRecord>(),
});

export const initialState = EstimateComparisonStateRecord();
export type EstimateComparisonState = typeof initialState;

const rootKey = "estimateComparisons";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.getIn([rootKey])) {
    state = state.setIn([rootKey], initialState);
  }

  switch (action.type) {
    case estimateComparisonActions.FETCH_ESTIMATE_COMPARISON: {
      if (state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        return state.setIn([rootKey, "byJobId", action.payload.jobId, "loading"], true);
      }

      return state.setIn(
        [rootKey, "byJobId", action.payload.jobId],
        estimateComparisonFromJSON({ job_id: action.payload.jobId, loading: true }),
      );
    }

    case estimateComparisonActions.RECEIVE_ESTIMATE_COMPARISON: {
      const record = estimateComparisonFromJSON(action.payload.estimateComparison);
      const jobId = record.job_id;

      state = state.setIn([rootKey, "byJobId", jobId], record);
      return state.setIn([rootKey, "lastSavedByJobId", jobId], record);
    }

    case estimateComparisonActions.SET_ESTIMATE_COMPARISON_LOADED:
      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        return state;
      }
      return state.setIn([rootKey, "byJobId", action.payload.jobId, "loading"], false);

    case estimateComparisonActions.BUILD_ESTIMATE_COMPARISON: {
      const existing: EstimateComparisonRecord = state.getIn([rootKey, "byJobId", action.payload.jobId]);
      const built = existing
        ? estimateComparisonFromJSON({ ...estimateComparisonToJSON(existing), estimate_comparison_links: [] })
        : estimateComparisonFromJSON({ job_id: action.payload.jobId });

      state = state.setIn([rootKey, "byJobId", action.payload.jobId], built);

      action.payload.estimateIds.forEach((estimateId) => {
        state = reducer(
          state,
          estimateComparisonActions.EstimateComparisonActions.addEstimateComparisonLink(
            estimateId,
            action.payload.jobId,
          ),
        );
      });

      return state;
    }

    case estimateComparisonActions.ADD_ESTIMATE_COMPARISON_LINK: {
      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        return state;
      }
      const links: List<EstimateComparisonLinkRecord> =
        state.getIn([rootKey, "byJobId", action.payload.jobId, "estimate_comparison_links"]) ?? List();
      const newLink = estimateComparisonLinkFromJSON({ estimate_id: action.payload.estimateId });

      return state.setIn([rootKey, "byJobId", action.payload.jobId, "estimate_comparison_links"], links.push(newLink));
    }

    case estimateComparisonActions.REMOVE_ESTIMATE_COMPARISON_LINK: {
      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        return state;
      }
      const links: List<EstimateComparisonLinkRecord> =
        state.getIn([rootKey, "byJobId", action.payload.jobId, "estimate_comparison_links"]) ?? List();

      if (links.filter((link) => !link._destroy).size <= 1) {
        return state;
      }

      const removedIndex = links.findIndex((link) => link.estimate_id === action.payload.estimateId && !link._destroy);
      if (removedIndex === -1) {
        return state;
      }

      const nextLinks =
        links.get(removedIndex)?.id === 0
          ? links.delete(removedIndex)
          : links.map((link, index) => (index === removedIndex ? link.set("_destroy", true) : link));

      return state.setIn([rootKey, "byJobId", action.payload.jobId, "estimate_comparison_links"], nextLinks);
    }

    case estimateComparisonActions.REMAP_ESTIMATE_COMPARISON_LINK: {
      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        return state;
      }
      const links: List<EstimateComparisonLinkRecord> =
        state.getIn([rootKey, "byJobId", action.payload.jobId, "estimate_comparison_links"]) ?? List();

      return state.setIn(
        [rootKey, "byJobId", action.payload.jobId, "estimate_comparison_links"],
        links.map((link) =>
          link.estimate_id === action.payload.fromEstimateId
            ? link.set("estimate_id", action.payload.toEstimateId)
            : link,
        ),
      );
    }

    case estimateComparisonActions.RESET_ESTIMATE_COMPARISON: {
      const jobId = action.payload.jobId;
      const lastSaved = state.getIn([rootKey, "lastSavedByJobId", jobId]);
      if (lastSaved) {
        return state.setIn([rootKey, "byJobId", jobId], lastSaved);
      }
      return state.deleteIn([rootKey, "byJobId", jobId]);
    }

    default:
      return state;
  }
};
